import Cookies from 'js-cookie';
import { AuthedHttp } from '../helpers/AuthedHttp';
import { isRegionalApiEnabled } from '../helpers/featureFlags/enableRegionalApi';
import { ResponseErrorsDto } from '../helperTypes/responseError';
import APIConfig from '../apiconfig';
import {
  redirectToLoginAppLogoutPage,
  redirectToLoginAppPage,
  shouldRedirectTo2faValidation,
} from '../helpers/redirectHelpers';
import { AccountRegion, UserTypeAndRegionDto } from '../@types/regionalApi';
import {
  getAccountRegionCookie,
  validateAccountRegion,
} from 'src/helpers/regionalApi';
import {
  ACCOUNT_REGION_COOKIE_KEY,
  MAKO_AUTH_TOKEN_COOKIE_KEY,
} from 'src/constants';
import { setCookie } from '../helpers/cookieHelpers';
import { is2faValidationRoute, isPublicRoute } from 'src/helpers/routes';
import { Analytics } from 'src/analytics';

export const userTypeEndpoint = 'user/type';
export const userTypeApiErrorMessage =
  'failed to get user type and region from /user/type endpoint';

export const getAccountRegion = async () => {
  if (!isRegionalApiEnabled()) {
    setCookie(ACCOUNT_REGION_COOKIE_KEY, AccountRegion.Global);
    return {
      region: AccountRegion.Global,
    } as UserTypeAndRegionDto;
  }

  const accountRegionCookie = getAccountRegionCookie();
  const accountRegionValidation = validateAccountRegion(accountRegionCookie);
  if (accountRegionValidation.success) {
    return {
      region: accountRegionValidation.accountRegion,
    } as UserTypeAndRegionDto;
  }

  const token = Cookies.get(MAKO_AUTH_TOKEN_COOKIE_KEY);
  const isOnPublicRoute = isPublicRoute();
  const isOn2faValidationRoute = is2faValidationRoute();

  if (isOn2faValidationRoute || isOnPublicRoute) {
    // do not need to set account region cookie, since all api requests
    // are directly routed to global api host.
    return {
      region: AccountRegion.Global,
    } as UserTypeAndRegionDto;
  }

  const response = await AuthedHttp.get<UserTypeAndRegionDto>(
    userTypeEndpoint,
    {
      forceGlobalApiHost: true,
    },
    undefined
  );

  const responseJson = await response.json();

  if (response.ok) {
    const { region } = responseJson as UserTypeAndRegionDto;
    setCookie(ACCOUNT_REGION_COOKIE_KEY, region);

    return responseJson;
  }

  if (response.status === 401) {
    console.error(
      'Unauthorized request to get user type and region for regional api'
    );
    if (token) {
      redirectToLoginAppLogoutPage();

      throw response;
    } else {
      redirectToLoginAppPage();

      throw response;
    }
  } else if (response.status === 403) {
    const errorResponseJson = responseJson as ResponseErrorsDto;
    if (shouldRedirectTo2faValidation(errorResponseJson, token)) {
      const redirectToQueryParam = encodeURIComponent(
        window.location.pathname + window.location.search
      );
      const redirectUrl = `${APIConfig.root}validate_2fa?redirect_to=${redirectToQueryParam}`;
      window.location.href = redirectUrl;

      throw response;
    }
  }

  Analytics.track({
    event: userTypeApiErrorMessage,
  });

  console.error(`${userTypeApiErrorMessage} defaulting to global API host`);
  // default to global region if there is an error
  // this is to prevent the app from breaking for parent users
  // if the api call fails and the cookie is not set
  setCookie(ACCOUNT_REGION_COOKIE_KEY, AccountRegion.Global);
  return {
    region: AccountRegion.Global,
  } as UserTypeAndRegionDto;
};
